import { useCallback, useEffect, useState } from "react";
import { client } from "../../../api/client";
import { extractApiError } from "../helpers";
import { AuditEntry, ListResponse } from "../types";

interface UseAuditLogResult {
  entries: AuditEntry[];
  count: number;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Loads audit log entries only when `active` becomes true — the hook is safe to
 * mount behind a tab that the user may never open.
 */
export default function useAuditLog(active: boolean): UseAuditLogResult {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page) });
      const resp = await client.get<ListResponse<AuditEntry>>(
        `/auth/admin/audit/?${params.toString()}`
      );
      setEntries(resp.data.results);
      setCount(resp.data.count);
    } catch (err) {
      setError(extractApiError(err, "Failed to load audit log."));
    } finally {
      setIsLoading(false);
    }
  }, [page]);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  return { entries, count, page, setPage, isLoading, error };
}
